import React, { useState, useEffect } from 'react';
import { useAuth, API } from './AuthContext';
import { Shield, Lock, Eye, EyeOff, Fingerprint, Sun, Moon } from 'lucide-react';
import { SecurityDateTime } from '../security/SecurityIntegration.jsx';
import '../security/SecurityStyles.css';

function AuthThemeToggle() {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('erp_theme');
    if (saved) return saved;
    return (window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
  });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);
  const toggle = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    document.documentElement.setAttribute('data-theme', next);
    localStorage.setItem('erp_theme', next);
  };
  const isDark = theme === 'dark';
  return (
    <button
      onClick={toggle}
      className="auth-theme-toggle"
      title={`Switch to ${isDark ? 'light' : 'dark'} theme`}
      data-testid="auth-theme-toggle"
    >
      {isDark ? <Moon size={13} /> : <Sun size={13} />}
      {isDark ? 'Dark' : 'Light'}
    </button>
  );
}

export default function LoginPage({ onForgotPassword, onSetup }) {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();
      if (data.success) {
        login(data.token, {
          username: data.username,
          email: data.email,
          isSuperAdmin: data.isSuperAdmin,
          mustChangePassword: data.mustChangePassword === true
        });
      } else if (data.error === 'NOT_SETUP') {
        onSetup();
      } else {
        setError(data.message || 'Login failed.');
      }
    } catch { setError('Connection error. Retry.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="auth-page">
      <AuthThemeToggle />

      {/* Top-right date time */}
      <div style={{ position: 'absolute', top: 20, right: 28, color: 'var(--auth-text-muted)' }}>
        <SecurityDateTime style={{ color: 'var(--auth-text-muted)' }} />
      </div>

      {/* Grid background pattern */}
      <div className="auth-page-grid" />

      <div className="auth-shell">
        {/* Logo */}
        <div className="auth-logo-wrap">
          <div className="brand-logo-wrap" style={{ justifyContent: 'center', marginBottom: 14, minHeight: 60 }}>
            <img
              src="/assets/retailcliq-logo-inverse.png"
              alt="RetailCLIQ"
              className="brand-logo brand-logo-dark"
              style={{ maxWidth: 240, maxHeight: 60 }}
              data-testid="brand-logo-auth"
            />
            <img
              src="/assets/retailcliq-logo-full.png"
              alt="RetailCLIQ"
              className="brand-logo brand-logo-light"
              style={{ maxWidth: 240, maxHeight: 60 }}
            />
          </div>
          <p className="auth-subtitle">RETAIL ERP · vEritEqcLiQ Security Module</p>
        </div>

        {/* Card */}
        <div className="auth-card">
          <h2 className="auth-card-heading">Administrator Login</h2>
          <p className="auth-card-sub">DNA-bound access control</p>

          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div>
              <label className="auth-label">Username</label>
              <input
                value={username}
                onChange={e => setUsername(e.target.value)}
                className="auth-input mono"
                autoComplete="username"
                data-testid="login-username"
              />
            </div>

            <div>
              <label className="auth-label">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your secure password"
                  className={`auth-input ${error ? 'error' : ''}`}
                  style={{ paddingRight: 44 }}
                  autoComplete="current-password"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(p => !p)}
                  className="auth-show-pass-btn"
                  data-testid="toggle-password-visibility"
                >
                  {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="auth-error-banner" data-testid="login-error">{error}</div>
            )}

            <button
              type="submit"
              disabled={loading || !password}
              className="auth-submit"
              data-testid="login-submit"
            >
              {loading ? (
                <><div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
                  borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} /> Authenticating...</>
              ) : (
                <><Lock size={14} /> Sign In to Security Module</>
              )}
            </button>
          </form>

          <div style={{ marginTop: 20, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Fingerprint size={13} color="var(--auth-text-muted)" />
            <button
              onClick={onForgotPassword}
              className="auth-link"
              data-testid="forgot-password-link"
            >
              Forgot Password?
            </button>
          </div>

          <div style={{ marginTop: 10, textAlign: 'center', fontSize: 11, color: 'var(--auth-text-muted, #94a3b8)' }}>
            Default username: <span style={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.05em' }}>HVSNKCVNM</span>
          </div>
        </div>

        {/* Footer */}
        <p className="auth-footer-note">
          MSN-CLIQ vEritEqcLiQ © 2024 — Proprietary System
        </p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
